import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Briefcase, Pencil, Trash2 } from 'lucide-react';

const BLANK_FORM = {
  title: '',
  category: '',
  max_weekly_workload: 0,
  workload_unit: 'periods',
  relief_workload_max: '',
  invigilation_workload_hours_per_week: '',
  invigilation_relief_workload_hours_per_week: '',
};

const JobTitlesPage = () => {
  const { token } = useContext(AuthContext);
  const [jobTitles, setJobTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(BLANK_FORM);

  useEffect(() => {
    fetchJobTitles();
  }, []);

  const fetchJobTitles = async () => {
    try {
      const response = await axios.get(`${API}/job-titles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobTitles(response.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Failed to load job titles:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData(BLANK_FORM);
    setDialogOpen(true);
  };

  const openEdit = (jt) => {
    setEditingId(jt.id);
    setFormData({
      title: jt.title || '',
      category: jt.category || '',
      max_weekly_workload: jt.max_weekly_workload ?? 0,
      workload_unit: jt.workload_unit || 'periods',
      relief_workload_max: jt.relief_workload_max ?? '',
      invigilation_workload_hours_per_week: jt.invigilation_workload_hours_per_week ?? '',
      invigilation_relief_workload_hours_per_week: jt.invigilation_relief_workload_hours_per_week ?? '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isEdit = Boolean(editingId);
    // Coerce empty strings → null for optional numerics; keep integers tight
    const payload = {
      title: formData.title,
      category: formData.category,
      max_weekly_workload: parseInt(formData.max_weekly_workload, 10) || 0,
      workload_unit: formData.workload_unit || 'periods',
      relief_workload_max: formData.relief_workload_max === '' ? null : parseInt(formData.relief_workload_max, 10),
      invigilation_workload_hours_per_week: formData.invigilation_workload_hours_per_week === '' ? null : parseFloat(formData.invigilation_workload_hours_per_week),
      invigilation_relief_workload_hours_per_week: formData.invigilation_relief_workload_hours_per_week === '' ? null : parseFloat(formData.invigilation_relief_workload_hours_per_week),
    };
    try {
      if (isEdit) {
        await axios.patch(`${API}/job-titles/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Job title updated successfully');
      } else {
        await axios.post(`${API}/job-titles`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Job title created successfully');
      }
      setDialogOpen(false);
      setEditingId(null);
      setFormData(BLANK_FORM);
      fetchJobTitles();
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
        (isEdit ? 'Failed to update job title' : 'Failed to create job title')
      );
    }
  };

  const handleDelete = async (jobTitleId) => {
    if (!window.confirm('Are you sure you want to delete this job title?')) return;
    try {
      await axios.delete(`${API}/job-titles/${jobTitleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Job title deleted');
      fetchJobTitles();
    } catch (error) {
      toast.error('Failed to delete job title');
    }
  };

  return (
    <div data-testid="job-titles-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900">Job Titles</h1>
          <p className="text-base text-slate-500 mt-2">Manage job titles and workload limits</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditingId(null);
            setFormData(BLANK_FORM);
          }
        }}>
          <DialogTrigger asChild>
            <Button
              className="bg-blue-900 hover:bg-blue-800"
              onClick={openCreate}
              data-testid="create-job-title-button"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Job Title
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingId ? 'Edit Job Title' : 'Create Job Title'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="title">Job Title</Label>
                <Input
                  id="title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Senior Teacher, HOD"
                  required
                  className="mt-1.5"
                  data-testid="title-input"
                />
              </div>

              <div>
                <Label htmlFor="category">Category</Label>
                <Input
                  id="category"
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="e.g., Teaching, Administration"
                  required
                  className="mt-1.5"
                  data-testid="category-input"
                />
              </div>

              <div>
                <Label htmlFor="workload">Max Weekly Workload</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input
                    id="workload"
                    type="number"
                    value={formData.max_weekly_workload}
                    onChange={(e) => setFormData({ ...formData, max_weekly_workload: parseInt(e.target.value, 10) || 0 })}
                    min="0"
                    required
                    className="flex-1"
                    data-testid="workload-input"
                  />
                  <select
                    value={formData.workload_unit}
                    onChange={(e) => setFormData({ ...formData, workload_unit: e.target.value })}
                    className="h-10 px-3 border border-slate-200 rounded-md bg-white text-sm"
                    data-testid="workload-unit-select"
                  >
                    <option value="periods">periods / week</option>
                    <option value="hours">hours / week</option>
                  </select>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">Normal teaching load cap. Unit applies to the three sub-caps below.</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="relief-max" className="text-xs text-slate-600">Max Relief / Week</Label>
                  <Input
                    id="relief-max"
                    type="number"
                    min="0"
                    placeholder="blank = use Max Weekly"
                    value={formData.relief_workload_max}
                    onChange={(e) => setFormData({ ...formData, relief_workload_max: e.target.value })}
                    className="mt-1.5"
                    data-testid="relief-max-input"
                  />
                </div>
                <div>
                  <Label htmlFor="invig-hours" className="text-xs text-slate-600">Invigilation Hours / Week</Label>
                  <Input
                    id="invig-hours"
                    type="number"
                    min="0"
                    step="0.5"
                    placeholder="optional"
                    value={formData.invigilation_workload_hours_per_week}
                    onChange={(e) => setFormData({ ...formData, invigilation_workload_hours_per_week: e.target.value })}
                    className="mt-1.5"
                    data-testid="invig-hours-input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="invig-relief-hours" className="text-xs text-slate-600">Invigilation Relief Hours / Week</Label>
                  <Input
                    id="invig-relief-hours"
                    type="number"
                    min="0"
                    step="0.5"
                    placeholder="optional"
                    value={formData.invigilation_relief_workload_hours_per_week}
                    onChange={(e) => setFormData({ ...formData, invigilation_relief_workload_hours_per_week: e.target.value })}
                    className="mt-1.5"
                    data-testid="invig-relief-hours-input"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-button">
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-900 hover:bg-blue-800" data-testid="submit-job-title-button">
                  {editingId ? 'Save Changes' : 'Create Job Title'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-slate-600">Loading job titles...</div>
      ) : jobTitles.length === 0 ? (
        <Card className="p-8 text-center" data-testid="no-job-titles-message">
          <Briefcase className="mx-auto h-12 w-12 text-slate-300 mb-4" />
          <p className="text-slate-500">No job titles created yet.</p>
        </Card>
      ) : (
        <div className="control-room-grid">
          {jobTitles.map((jobTitle) => (
            <Card key={jobTitle.id} className="dashboard-card p-6" data-testid="job-title-card">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-slate-900 mb-1 truncate">{jobTitle.title}</h3>
                  <p className="text-sm text-slate-500">{jobTitle.category}</p>
                </div>
                <div className="flex items-center gap-1 ml-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEdit(jobTitle)}
                    className="text-slate-600 hover:text-blue-700 hover:bg-blue-50"
                    data-testid="edit-job-title-button"
                    aria-label={`Edit ${jobTitle.title}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(jobTitle.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="delete-job-title-button"
                    aria-label={`Delete ${jobTitle.title}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="pt-4 border-t border-slate-200 space-y-2">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Max Weekly Workload</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {jobTitle.max_weekly_workload}
                    <span className="text-xs text-slate-500 font-normal ml-1">
                      {jobTitle.workload_unit || 'periods'} / week
                    </span>
                  </p>
                </div>
                {(jobTitle.relief_workload_max != null || jobTitle.invigilation_workload_hours_per_week != null || jobTitle.invigilation_relief_workload_hours_per_week != null) && (
                  <div className="grid grid-cols-3 gap-2 text-[11px] pt-2 border-t border-slate-100" data-testid="jt-workload-sub">
                    <div>
                      <p className="text-slate-400 uppercase tracking-wide">Relief</p>
                      <p className="font-semibold text-slate-900">
                        {jobTitle.relief_workload_max ?? '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-400 uppercase tracking-wide">Invig hrs</p>
                      <p className="font-semibold text-slate-900">
                        {jobTitle.invigilation_workload_hours_per_week ?? '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-400 uppercase tracking-wide">Invig relief</p>
                      <p className="font-semibold text-slate-900">
                        {jobTitle.invigilation_relief_workload_hours_per_week ?? '—'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default JobTitlesPage;
